import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog'
import { Input } from './ui/input'
import { Button } from './ui/button'
import { Label } from './ui/label'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import Select from 'react-select'
import type { MultiValue } from 'react-select'

import { SelectProject, useSelectProjectsQuery } from '@/redux/service/project'
import { useUpdateClientMutation } from '@/redux/service/client'
import toast from 'react-hot-toast'

/* ===================== TYPES ===================== */

interface Project {
  // ClickUp task id (project_task_id), not projects.project_id.
  project_id: string
  project_name: string
}

interface Client {
  client_id: string
  client_name: string
  projects: Project[]
}

interface ClientFormValues {
  client_name: string
  project_ids: string[]
}

interface SelectOption {
  value: string
  label: string
}

interface ClientModelProps {
  data?: Client | null
  projects?: string[]
  isOpen: boolean
  isClose: () => void
}

const ClientModel: React.FC<ClientModelProps> = ({
  data,
  projects,
  isOpen,
  isClose,
}) => {
  // pending: true — a client can be linked to a project regardless of its
  // setup/archive status, so the picker must include all of them or an
  // existing assignment to a pending/archived project silently disappears.
  const { data: allProjects, isLoading } = useSelectProjectsQuery({
    pending: true,
  })

  const [updateClient] = useUpdateClientMutation()

  const isEditMode = Boolean(data?.client_id)

  const formik = useFormik<ClientFormValues>({
    enableReinitialize: true,
    initialValues: {
      client_name: data?.client_name ?? '',
      project_ids: data?.projects?.map(p => p.project_id) ?? [],
    },
    validationSchema: Yup.object({
      client_name: Yup.string().trim().required('Client name is required'),
      project_ids: Yup.array()
        .of(Yup.string().required())
        .min(1, 'Select at least one project'),
    }),
    onSubmit: async (values, { setSubmitting, setFieldError }) => {
      try {
        if (isEditMode && data) {
          await updateClient({
            ...values,
            id: data.client_id,
          }).unwrap()
        }

        formik.resetForm()
        isClose()
      } catch (error) {
        const message =
          (error as { data?: { message?: string } })?.data?.message ||
          'Project already assigned to another client'

        toast.error(message)

        if (message.toLowerCase().includes('project')) {
          setFieldError('project_ids', message)
        } else {
          setFieldError('client_name', message)
        }
      } finally {
        setSubmitting(false)
      }
    },
  })
  const currentClientProjectIds = data?.projects?.map(p => p.project_id) ?? []

  const projectOptions: SelectOption[] =
    allProjects?.data
      ?.filter((p: SelectProject) => {
        const projectId = String(p.project_id)
        if (projects?.includes(projectId)) {
          return currentClientProjectIds.includes(projectId)
        }
        return true
      })
      .map(p => ({
        value: String(p.project_id),
        label: p.list_name,
      })) ?? []

  return (
    <Dialog
      open={isOpen}
      onOpenChange={open => {
        if (!open) isClose()
      }}
    >
      <DialogContent className='max-w-md p-6 bg-background border border-border rounded-xl shadow-2xl text-foreground'>
        <DialogHeader className='space-y-1'>
          <DialogTitle className='text-xl font-bold text-foreground'>
            {isEditMode ? 'Update Client' : 'Create Client'}
          </DialogTitle>
          <p className='text-sm text-muted-foreground'>
            {isEditMode
              ? 'Modify the client details and assigned projects.'
              : 'Add a new client to the dashboard.'}
          </p>
        </DialogHeader>

        <form onSubmit={formik.handleSubmit} className='mt-6 space-y-6'>
          {/* CLIENT NAME */}
          <div className='space-y-2'>
            <Label
              htmlFor='client_name'
              className='text-xs font-bold uppercase tracking-wider'
            >
              Client Name <span className='text-red-500'>*</span>
            </Label>
            <Input
              id='client_name'
              name='client_name'
              placeholder='Enter client name'
              value={formik.values.client_name}
              onChange={formik.handleChange}
              className='h-10  border-border text-foreground focus:border-primary focus:ring-0 transition-all rounded-lg'
            />
            {formik.touched.client_name && formik.errors.client_name && (
              <p className='text-xs text-destructive font-medium'>
                {formik.errors.client_name}
              </p>
            )}
          </div>

          {/* PROJECTS */}
          <div className='space-y-2'>
            <Label className='text-xs font-bold uppercase tracking-wider '>
              Assigned Projects <span className='text-red-500'>*</span>
            </Label>
            <Select<SelectOption, true>
              isMulti
              isLoading={isLoading}
              options={projectOptions}
              value={projectOptions.filter(opt =>
                formik.values.project_ids.includes(opt.value)
              )}
              onChange={(selected: MultiValue<SelectOption>) =>
                formik.setFieldValue(
                  'project_ids',
                  selected.map(s => s.value)
                )
              }
              placeholder='Select projects...'
              className={`text-sm  border rounded-sm hover:border-primary py-1`}
              classNames={{
                control: ({ isFocused }) =>
                  `!bg-background   !min-h-[40px] !rounded-lg !shadow-none transition-all ${
                    isFocused ? '!border-primary !ring-0' : ''
                  }`,
                menu: () =>
                  '!bg-popover !border-border !shadow-2xl !rounded-lg !mt-2 !overflow-hidden',
                option: ({ isFocused, isSelected }) =>
                  `!px-3 !py-2 !cursor-pointer transition-colors ${
                    isSelected
                      ? '!bg-primary !text-primary-foreground'
                      : isFocused
                        ? '!bg-accent !text-accent-foreground'
                        : '!text-popover-foreground'
                  }`,
                multiValue: () =>
                  '!bg-secondary !text-secondary-foreground !rounded-md !px-1',
                multiValueLabel: () =>
                  '!text-secondary-foreground !text-xs !font-medium',
                multiValueRemove: () =>
                  '!text-secondary-foreground hover:!bg-muted !rounded-md !transition-colors',
                placeholder: () => '!text-muted-foreground',
                input: () => '!text-foreground',
                singleValue: () => '!text-foreground',
              }}
              styles={{
                control: base => ({
                  ...base,
                  border: 0,
                }),
              }}
            />
            {formik.touched.project_ids && formik.errors.project_ids && (
              <p className='text-xs text-destructive font-medium'>
                {formik.errors.project_ids}
              </p>
            )}
          </div>

          {/* ACTIONS */}
          <div className='flex justify-end gap-3 pt-4'>
            <Button
              type='button'
              variant='outline'
              onClick={isClose}
              className='px-6 border-border text-muted-foreground hover:bg-accent hover:text-accent-foreground rounded-lg transition-all'
            >
              Cancel
            </Button>

            <Button
              type='submit'
              disabled={formik.isSubmitting}
              className='px-8 rounded-lg transition-all'
            >
              {formik.isSubmitting
                ? 'Saving...'
                : isEditMode
                  ? 'Update'
                  : 'Create'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}

export default ClientModel
